/**
 * Secure fetch wrapper for /web/api/* calls.
 *
 * Security guarantees this layer enforces:
 *   1. `credentials: "include"` — the HttpOnly `ns_sid` cookie is
 *      attached to every request. JS never sees it directly.
 *   2. Reads the non-HttpOnly `ns_csrf` cookie and copies its value
 *      into the `X-CSRF-Token` header on every state-changing call
 *      (POST/PUT/PATCH/DELETE). Backend rejects with 403 if missing
 *      or wrong (compare_digest).
 *   3. Bodies are always JSON. Never builds raw HTML strings.
 *   4. On 401 we route the user back to /login — the session is
 *      already gone server-side, no point keeping the SPA pretending.
 *   5. Throws `ApiError` with the parsed `detail` on any non-2xx,
 *      so callers always see a typed failure path.
 */

const CSRF_COOKIE = "ns_csrf";
const CSRF_HEADER = "X-CSRF-Token";

const STATE_CHANGING = new Set(["POST", "PUT", "PATCH", "DELETE"]);

export class ApiError extends Error {
  constructor(
    public status: number,
    public detail: unknown,
    message?: string,
  ) {
    super(message ?? (typeof detail === "string" ? detail : `HTTP ${status}`));
    this.name = "ApiError";
  }
}

function readCookie(name: string): string | null {
  // Manual parse — we don't trust libraries for security-critical lookups.
  const target = `${name}=`;
  for (const part of document.cookie.split(";")) {
    const trimmed = part.trim();
    if (trimmed.startsWith(target)) {
      return decodeURIComponent(trimmed.slice(target.length));
    }
  }
  return null;
}

interface ApiOptions {
  body?: unknown;
  /** Custom headers (rarely needed). */
  headers?: Record<string, string>;
  /** Don't redirect to /login on 401 — useful for the login page itself. */
  noRedirectOn401?: boolean;
  /** AbortSignal forwarded to fetch. */
  signal?: AbortSignal;
}

let _onUnauthorized: () => void = () => {
  // Default: hard redirect. The Router will replace this once mounted.
  if (typeof window !== "undefined" && window.location.pathname !== "/login") {
    window.location.href = "/login";
  }
};

export function setUnauthorizedHandler(fn: () => void): void {
  _onUnauthorized = fn;
}

async function call<T>(
  method: string,
  path: string,
  opts: ApiOptions = {},
): Promise<T> {
  if (!path.startsWith("/")) {
    throw new Error("api path must start with /");
  }

  const headers: Record<string, string> = {
    Accept: "application/json",
    ...(opts.headers ?? {}),
  };

  if (STATE_CHANGING.has(method)) {
    const csrf = readCookie(CSRF_COOKIE);
    if (csrf) {
      headers[CSRF_HEADER] = csrf;
    } else if (path !== "/web/api/login") {
      // No CSRF cookie means either (a) we're not logged in yet, or
      // (b) the cookie was set Secure=true over an http origin and
      // the browser silently dropped it. Surface a clear error so the
      // user knows to re-login / fix WEB_COOKIE_SECURE rather than
      // staring at an opaque 403.
      throw new ApiError(
        0,
        "missing_csrf_cookie",
        "CSRF cookie missing — sign out and sign back in. " +
          "If this persists in dev, set WEB_COOKIE_SECURE=false in .env.",
      );
    }
  }

  let body: BodyInit | undefined;
  if (opts.body !== undefined) {
    body = JSON.stringify(opts.body);
    headers["Content-Type"] = "application/json";
  }

  const res = await fetch(path, {
    method,
    credentials: "include",
    headers,
    body,
    signal: opts.signal,
  });

  // 204 No Content → return null cast as T
  if (res.status === 204) return null as T;

  let payload: unknown;
  const ct = res.headers.get("content-type") ?? "";
  if (ct.includes("application/json")) {
    payload = await res.json().catch(() => undefined);
  } else {
    payload = await res.text().catch(() => undefined);
  }

  if (!res.ok) {
    if (res.status === 401 && !opts.noRedirectOn401) {
      _onUnauthorized();
    }
    const detail =
      typeof payload === "object" && payload !== null && "detail" in payload
        ? (payload as { detail: unknown }).detail
        : payload;
    throw new ApiError(res.status, detail);
  }

  return payload as T;
}

export const api = {
  get: <T>(path: string, opts?: ApiOptions) => call<T>("GET", path, opts),
  post: <T>(path: string, opts?: ApiOptions) => call<T>("POST", path, opts),
  put: <T>(path: string, opts?: ApiOptions) => call<T>("PUT", path, opts),
  patch: <T>(path: string, opts?: ApiOptions) => call<T>("PATCH", path, opts),
  delete: <T>(path: string, opts?: ApiOptions) => call<T>("DELETE", path, opts),
};
